import { ChevronLeft } from '@mui/icons-material';
import { Box, Step, StepLabel, Stepper, IconButton, Button } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useCompanyNavigate } from '@lendica/hooks';
import { FeatureLayout, HeaderToolbar, DropzoneFile } from '@lendica/components';
import { StepProgressContent } from './StepProgressContent';
import { useFileStatus } from '../hooks/useInvoiceUploadProgress';
import { InvoiceUploadManager } from '../services/InvoiceUploadManager';

export interface ProcessInvoiceProps {
    files: DropzoneFile[];
    onExit(): void;
}

export const UploadInvoice: FC<ProcessInvoiceProps> = ({ files, onExit }) => {
    const [activeStep, setActiveStep] = useState(0);
    const navigate = useCompanyNavigate();
    const isUploadInProgress = files.some(item => !item.key);
    const hasUploadError = files.some(item => item.error);
    const { status, invoiceId } = useFileStatus(files[0]);

    useEffect(() => {
        if (!isUploadInProgress && !hasUploadError) {
            setActiveStep(1);
        }
    }, [isUploadInProgress, hasUploadError]);

    // Kick off processing via manager once we reach step 1
    useEffect(() => {
        if (activeStep === 1 && files[0]?.key) {
            InvoiceUploadManager.startProcessing(files[0]);
        }
    }, [activeStep, files]);

    // Navigate when processing succeeds
    useEffect(() => {
        if (status === 'processing-success' && invoiceId) {
            navigate(`/fundnow/invoice/${invoiceId}/confirm`);
        }
    }, [status, invoiceId]);

    return (
        <FeatureLayout>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ px: 2.5 }}>
                    <HeaderToolbar
                        label="Upload Invoice"
                        leftAction={
                            hasUploadError ? (
                                <IconButton size="large" onClick={onExit}>
                                    <ChevronLeft fontSize="inherit" />
                                </IconButton>
                            ) : undefined
                        }
                    />
                </Box>
                <Box sx={{ px: 4, pt: 1, flexGrow: 1, overflowY: 'auto' }}>
                    <Stepper activeStep={activeStep} orientation="vertical">
                        <Step>
                            <StepLabel>File upload</StepLabel>
                            {hasUploadError ? (
                                <StepProgressContent
                                    label="Files failed to upload."
                                    description="Please go back and try again."
                                    error
                                />
                            ) : (
                                <StepProgressContent
                                    label="Files are uploading..."
                                    description="Please wait for invoices to finish uploading. Do not leave the page."
                                />
                            )}
                        </Step>
                        <Step>
                            <StepLabel>Processing invoice</StepLabel>
                            <StepProgressContent
                                label="Uploaded and processing..."
                                description="It takes a few moments to process the invoice. You can continue to add invoices and come back in a few minutes to confirm the details."
                            />
                        </Step>
                        <Step>
                            <StepLabel>Confirm details</StepLabel>
                        </Step>
                    </Stepper>
                </Box>
                {activeStep === 1 && (
                    <Box sx={{ px: 4, py: 1.5 }}>
                        <Button variant="contained" fullWidth onClick={() => navigate('../..')}>
                            Ok
                        </Button>
                    </Box>
                )}
            </Box>
        </FeatureLayout>
    );
};
